import { Children, Component, ReturnComponent } from '@/utils/reactTypes';
import { reportNetworkError } from '@/utils/tracking';
import { Box, CircularProgress, Skeleton } from '@mui/material';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { AxleQueryFailedError } from './apiHelpers';

export const SimpleSkeleton = ({
    height,
    width,
    padding = 0,
}: {
    height: string;
    width?: string | undefined;
    padding?: number;
}): ReturnComponent => (
    <Box padding={padding}>
        <Skeleton variant={'rectangular'} height={height} width={width} sx={{ borderRadius: 1 }} />
    </Box>
);

// Avoid the spinner!
//  https://www.lukew.com/ff/entry.asp?1797
const Spinner = (): ReturnComponent => (
    <CircularProgress
        style={{
            margin: '1em',
        }}
    />
);

export const HorizontallyCenteredSpinner = (): ReturnComponent => (
    <Box style={{ width: '100%', textAlign: 'center' }}>
        <Spinner />
    </Box>
);

export const SECTION_SKELETON_HEIGHT = '20vh';
export const DIALOG_SKELETON_HEIGHT = '40vh';
export const PAGE_BODY_SKELETON_HEIGHT = '60vh';
export const FULL_PAGE_SKELETON_HEIGHT = '80vh';

// As a fallback, we can show a skeleton with a certain height, a spinner, or a custom fallback component.
//  Most of the time, you should just set `skeletonHeight` and get a nice skeleton fallback.
export const AxleSuspense = ({
    children,
    skeletonHeight,
    skeletonWidth,
    skeletonPadding = 0,
    spinner = false,
    customFallback,
}: {
    children: Children;
} & (
    | {
          skeletonHeight: string;
          skeletonWidth?: string;
          skeletonPadding?: number;
          spinner?: false;
          customFallback?: undefined;
      }
    | {
          skeletonHeight?: undefined;
          skeletonWidth?: undefined;
          skeletonPadding?: undefined;
          spinner: true;
          customFallback?: undefined;
      }
    | {
          skeletonHeight?: undefined;
          skeletonWidth?: undefined;
          skeletonPadding?: undefined;
          spinner?: false;
          customFallback: Component;
      }
)) => {
    let fallback;
    if (skeletonHeight) {
        fallback = (
            <SimpleSkeleton
                height={skeletonHeight}
                width={skeletonWidth}
                padding={skeletonPadding}
            />
        );
    } else if (spinner) {
        fallback = <HorizontallyCenteredSpinner />;
    } else if (customFallback !== undefined) {
        fallback = customFallback;
    } else {
        throw new Error('No suspense fallback specified.');
    }

    // There’s this weird thing where React Query forces the retryOnMount option to be false
    //  when the suspense option is enabled. This means that queries that failed are not retried
    //  when we navigate back to the app page from a different app page, or if we open a dialog
    //  a second time. To work around this, we need to call this reset() function.
    // The offending code in question:
    //  https://github.com/TanStack/query/blob/34ca5581217dae4133046f6cfbc746a22be14dd9/packages/react-query/src/errorBoundaryUtils.ts#L29
    // A Github issue on this:
    //  https://github.com/TanStack/query/discussions/3487#discussioncomment-2576714
    const { reset } = useQueryErrorResetBoundary();
    reset();

    return (
        <Suspense fallback={fallback}>
            <ErrorBoundary
                // This error boundary is for catching AxleQueryFailedError errors and rendering
                //  the same fallback as the suspended state. This way, if a query fails, the
                //  component tree remains in a suspended-looking state.
                // We do not use <Sentry.ErrorBoundary/> as we want  control over which errors
                //  get sent to Sentry.
                fallback={fallback}
                onError={(error) => {
                    if (error instanceof AxleQueryFailedError) {
                        // Some AxleQueryFailedError should be sent to Sentry, others shouldn’t be
                        //  based on their `shouldLogToSentry` property.
                        if (error.shouldLogToSentry) {
                            // Report with the structured axios context (url, code, timeout,
                            //  duration, status, response) so the network-error detail survives
                            //  in Sentry and Datadog instead of being swallowed.
                            reportNetworkError(error, error.axiosContext);
                        } else {
                            // TODO: Seems like Sentry gets sent the error even if we don't call
                            //  Sentry.captureException() explicitly.
                        }
                    } else {
                        // If we get an error that’s NOT an AxleQueryFailedError, we rethrow it
                        //  so it can be handled by a different error boundary.
                        throw error;
                    }
                }}
            >
                {children}
            </ErrorBoundary>
        </Suspense>
    );
};
